import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventContext = createContext();

export function EventProvider({ children }) {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      // Include auth token if available so admins can see all events
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/events`, { headers });
      const eventList = response.data;
      
      if (eventList.length === 0) {
        setLoading(false);
        return;
      }
      
      setEvents(eventList);
      
      // Select current event based on dates
      const selectedEvent = selectCurrentEventByDate(eventList);
      setCurrentEvent(selectedEvent);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectCurrentEventByDate = (eventList) => {
    const now = new Date();
    
    // Find events where today is between start_date and end_date
    const currentEvents = eventList.filter(event => {
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      return now >= startDate && now <= endDate && event.status === 'active';
    });
    
    // If multiple current events, return most recent by start_date
    if (currentEvents.length > 0) {
      currentEvents.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      return currentEvents[0];
    }
    
    // Fallback: return most recent active event
    const activeEvents = eventList.filter(e => e.status === 'active');
    if (activeEvents.length > 0) {
      activeEvents.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      return activeEvents[0];
    }
    
    return eventList[0];
  };

  const switchEvent = (event) => {
    setCurrentEvent(event);
  };

  return (
    <EventContext.Provider value={{ currentEvent, setCurrentEvent, events, switchEvent, loading }}>
      {children}
    </EventContext.Provider>
  );
}

export function useEvent() {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within EventProvider');
  }
  return context;
}
